import { useEffect, useState } from 'react';

import Modal from '@ui/modal/modal';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import CustomSelect from '@ui/select/custom-select/custom-select';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import GlobalTemplateTypeSelector from '@ui/global-templates/global-template-type-selector';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectProjectGlobalTemplateType, selectWorkingProjectType } from '@/store/slices/project/selectors';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { EventType, EventTypeLabels } from '@/types/projects.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

const eventTypeOptions: TReactSelectOption[] = Object.values(EventType).map((type) => ({
  label: EventTypeLabels[type]?.name || type,
  value: type,
}));

type EditGlobalTemplateTypeModalProps = {
  open: boolean;
  handleClose: () => void;
};

const EditGlobalTemplateTypeModal = ({ open, handleClose }: EditGlobalTemplateTypeModalProps) => {
  const dispatch = useAppDispatch();
  const currentGlobalTemplateType = useAppSelector(selectProjectGlobalTemplateType);
  const currentEventType = useAppSelector(selectWorkingProjectType);

  const [globalTemplateType, setGlobalTemplateType] = useState<string>(currentGlobalTemplateType || '');
  const [selectedEventType, setSelectedEventType] = useState<EventType>(currentEventType || EventType.Webinar);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGlobalTemplateType(currentGlobalTemplateType || '');
    setSelectedEventType(currentEventType || EventType.Webinar);
  }, [open, currentGlobalTemplateType, currentEventType]);

  const handleSave = () => {
    const trimmed = globalTemplateType.trim();
    if (!trimmed) return;
    setSaving(true);
    dispatch(
      saveWorkingProjectByFields({
        global_template_type: trimmed,
        event_type: selectedEventType,
      })
    );
    setSaving(false);
    handleClose();
  };

  const selectedEventTypeOption = eventTypeOptions.find((o) => o.value === selectedEventType) || eventTypeOptions[0];

  const handleEventTypeChange = (options: TReactSelectOption[]) => {
    const next = options[0];
    if (!next) return;
    setSelectedEventType(next.value as EventType);
  };

  return (
    <Modal
      open={open}
      onRequestClose={handleClose}
      customSize={{
        height: 'auto',
        minHeight: '200px',
        width: '640px',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 64px)',
      }}
      RenderHeaderContent={() => (
        <Container background="transparent" padding="24px 40px">
          <Text tag="h4" className="H4">
            Global Template Type
          </Text>
        </Container>
      )}
      RenderFooterContent={() => (
        <Container
          background="transparent"
          className="flex fd-row jc-flex-end ai-center gap-8"
          padding="0 40px 24px 40px"
        >
          <Button
            design="LocalBttnSec"
            onClick={handleClose}
            disabled={saving}
            className="L1 padding-v-16 padding-h-24"
          >
            Cancel
          </Button>
          <Button
            backgroundColor={getThemeColorCSSVariable()}
            padding="16px 24px"
            width="auto"
            disabled={!globalTemplateType.trim() || saving}
            onClick={handleSave}
            loading={saving}
            hoverOpacity={0.8}
          >
            <Text color={getThemeColorContrastCSSVariable()} className="L1">
              Save
            </Text>
          </Button>
        </Container>
      )}
    >
      <Container background="transparent" padding="0 40px 24px 40px" className="flex-column gap-16">
        <GlobalTemplateTypeSelector
          bypassFetchGlobalTemplateTypes={!open}
          onChange={(value) => setGlobalTemplateType(value)}
        />

        <div>
          <Text className="S3 margin-b-5 margin-l-16" design="GlobalIcoText1">
            Setting the event/project type determines which channel types this global template can be used in. For
            example, select Webinar for Webinar+ channels, or Townhall for Townhall channels.
          </Text>
          <label className="margin-b-5 margin-l-16 P2" htmlFor="global-template-event-type">
            Event / Project Type *
          </label>
          <CustomSelect
            id="global-template-event-type"
            hideHeader
            labelHeight="40px"
            isMultiSelect={false}
            isSearchable={false}
            permanentDropdownIcon
            Placeholder="Select an event/project type"
            options={eventTypeOptions}
            controlledOptions={[selectedEventTypeOption]}
            onChange={handleEventTypeChange}
          />
        </div>
      </Container>
    </Modal>
  );
};

export default EditGlobalTemplateTypeModal;
